import { App, Button, Descriptions, Drawer, Select, Space, Table, Tag, Typography, Upload } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { seededName } from '@/shared/lib/seededName'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { formatDate, formatDateTime } from '@/shared/lib/format'
import { formatMoneyValue, money } from '@/shared/lib/money'
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_BYTES,
  PURCHASE_ATTACHMENT_KINDS,
  fetchPurchase,
  fetchPurchasePayments,
  fetchPurchaseReceipts,
  uploadSupplierAttachment,
  type AttachmentKind,
} from './supplierApi'
import { PaymentStatusTag, PurchaseStateTag } from './PurchaseTags'

export function PurchaseDetailDrawer({ purchaseId, onClose }: { purchaseId: string | null; onClose: () => void }) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const [kind, setKind] = useState<AttachmentKind>('INVOICE')
  const enabled = purchaseId !== null

  const purchase = useQuery({
    queryKey: queryKeys.supplier.purchase(purchaseId ?? ''),
    queryFn: ({ signal }) => fetchPurchase(purchaseId ?? '', signal),
    enabled,
  })
  const receipts = useQuery({
    queryKey: queryKeys.supplier.receipts(purchaseId ?? ''),
    queryFn: ({ signal }) => fetchPurchaseReceipts(purchaseId ?? '', signal),
    enabled,
  })
  const payments = useQuery({
    queryKey: queryKeys.supplier.payments(purchaseId ?? ''),
    queryFn: ({ signal }) => fetchPurchasePayments(purchaseId ?? '', signal),
    enabled,
  })

  const upload = useMutation({
    mutationFn: (file: File) => {
      const data = purchase.data
      if (!data) throw new Error('purchase')
      return uploadSupplierAttachment({ owner: 'purchase', supplierId: data.supplier.id, purchaseId: data.id }, kind, file)
    },
    onSuccess: () => {
      void message.success(t('supplier.attachments.uploaded'))
      void queryClient.invalidateQueries({ queryKey: queryKeys.supplier.all })
    },
    onError: (error) => void message.error(translateError(error, t)),
  })

  const data = purchase.data
  const currency = data?.currency ?? ''

  return (
    <Drawer open={enabled} onClose={onClose} width={860} title={data ? `${data.number} · ${data.supplier.name}` : t('common.loading')}>
      {purchase.isError ? <Typography.Text type="danger">{translateError(purchase.error, t)}</Typography.Text> : null}
      {data ? (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label={t('purchase.field.state')}>
              <PurchaseStateTag state={data.state} />
            </Descriptions.Item>
            <Descriptions.Item label={t('purchase.field.paymentStatus')}>
              <PaymentStatusTag status={data.paymentStatus} />
            </Descriptions.Item>
            <Descriptions.Item label={t('purchase.field.invoiceNo')}>{data.invoiceNo ?? t('common.dash')}</Descriptions.Item>
            <Descriptions.Item label={t('purchase.field.invoiceDate')}>{formatDate(data.invoiceDate)}</Descriptions.Item>
            <Descriptions.Item label={t('purchase.field.dueDate')}>{formatDate(data.dueDate)}</Descriptions.Item>
            <Descriptions.Item label={t('purchase.field.orgNode')}>{data.orgNode?.name ?? t('common.dash')}</Descriptions.Item>
            <Descriptions.Item label={t('purchase.field.warehouse')}>{data.warehouse?.name ?? t('common.dash')}</Descriptions.Item>
            <Descriptions.Item label={t('purchase.field.total')}>{formatMoneyValue(data.total)}</Descriptions.Item>
            <Descriptions.Item label={t('purchase.field.recognized')}>{formatMoneyValue(data.recognized)}</Descriptions.Item>
            <Descriptions.Item label={t('purchase.field.paid')}>{formatMoneyValue(data.paid)}</Descriptions.Item>
            <Descriptions.Item label={t('purchase.field.remaining')}>{formatMoneyValue(data.remaining)}</Descriptions.Item>
            <Descriptions.Item label={t('purchase.field.createdBy')}>
              {`${data.createdBy.name} · ${formatDateTime(data.createdAt)}`}
            </Descriptions.Item>
            <Descriptions.Item label={t('purchase.field.verifiedBy')}>
              {data.verifiedBy ? `${data.verifiedBy.name} · ${formatDateTime(data.verifiedAt)}` : t('common.dash')}
            </Descriptions.Item>
            <Descriptions.Item label={t('purchase.field.verificationNote')}>{data.verificationNote ?? t('common.dash')}</Descriptions.Item>
            {data.cancelReason ? (
              <Descriptions.Item label={t('purchase.field.cancelReason')} span={2}>
                {`${data.cancelReason} · ${data.cancelledBy?.name ?? ''} · ${formatDateTime(data.cancelledAt)}`}
              </Descriptions.Item>
            ) : null}
            {data.memo ? (
              <Descriptions.Item label={t('purchase.field.memo')} span={2}>
                {data.memo}
              </Descriptions.Item>
            ) : null}
          </Descriptions>

          <Table
            size="small"
            rowKey="id"
            pagination={false}
            title={() => t('purchase.field.lines')}
            dataSource={data.lines}
            columns={[
              { key: 'no', title: '#', width: 40, render: (_, line) => line.lineNo },
              { key: 'kind', title: t('purchase.field.kind'), render: (_, line) => <Tag>{t(`purchase.lineKind.${line.kind}`)}</Tag> },
              { key: 'description', title: t('purchase.field.description'), render: (_, line) => line.description },
              { key: 'account', title: t('purchase.field.account'), render: (_, line) => (line.account ? `${line.account.code} · ${seededName('accounts', line.account.code, line.account.name, t)}` : t('common.dash')) },
              { key: 'quantity', title: t('purchase.field.quantity'), align: 'right', render: (_, line) => line.quantity },
              {
                key: 'received',
                title: t('purchase.receive.received'),
                align: 'right',
                render: (_, line) => (line.kind === 'STOCK' ? line.receivedQuantity : t('common.dash')),
              },
              { key: 'price', title: t('purchase.field.unitPrice'), align: 'right', render: (_, line) => formatMoneyValue(line.unitPrice) },
              { key: 'amount', title: t('purchase.field.amount'), align: 'right', render: (_, line) => formatMoneyValue(line.amount) },
            ]}
          />

          <Table
            size="small"
            rowKey="id"
            pagination={false}
            loading={receipts.isPending}
            title={() => t('purchase.receive.history')}
            locale={{ emptyText: t('purchase.receive.empty') }}
            dataSource={receipts.data ?? []}
            columns={[
              { key: 'at', title: t('purchase.receive.at'), render: (_, row) => formatDateTime(row.receivedAt) },
              { key: 'by', title: t('purchase.receive.by'), render: (_, row) => row.receivedBy.name ?? t('common.dash') },
              { key: 'warehouse', title: t('purchase.field.warehouse'), render: (_, row) => row.warehouse?.name ?? t('common.dash') },
              {
                key: 'lines',
                title: t('purchase.field.lines'),
                render: (_, row) => row.lines.map((line) => `${line.description} × ${line.quantity}`).join('; '),
              },
              { key: 'amount', title: t('purchase.field.amount'), align: 'right', render: (_, row) => formatMoneyValue(money(row.amountMinor, currency)) },
            ]}
          />

          <Table
            size="small"
            rowKey="id"
            pagination={false}
            loading={payments.isPending}
            title={() => t('purchase.payment.history')}
            locale={{ emptyText: t('purchase.payment.empty') }}
            dataSource={payments.data ?? []}
            columns={[
              { key: 'paidOn', title: t('purchase.payment.paidOn'), render: (_, row) => formatDate(row.paidOn) },
              { key: 'amount', title: t('purchase.payment.amount'), align: 'right', render: (_, row) => formatMoneyValue(row.amount) },
              { key: 'account', title: t('purchase.payment.account'), render: (_, row) => `${row.account.code} · ${seededName('accounts', row.account.code, row.account.name, t)}` },
              { key: 'reference', title: t('purchase.payment.reference'), render: (_, row) => row.reference ?? t('common.dash') },
              { key: 'by', title: t('purchase.payment.by'), render: (_, row) => row.paidBy.name },
            ]}
          />

          <Can permission="purchase.create">
            <Space>
              <Select<AttachmentKind>
                value={kind}
                onChange={setKind}
                style={{ minWidth: 160 }}
                options={PURCHASE_ATTACHMENT_KINDS.map((value) => ({ value, label: t(`supplier.attachmentKind.${value}`) }))}
              />
              <Upload
                accept={ATTACHMENT_ACCEPT}
                showUploadList={false}
                beforeUpload={(file) => {
                  if (file.size > ATTACHMENT_MAX_BYTES) {
                    void message.warning(t('supplier.attachments.tooLarge'))
                    return false
                  }
                  upload.mutate(file)
                  return false
                }}
              >
                <Button icon={<UploadOutlined />} loading={upload.isPending}>
                  {t('purchase.attachInvoice')}
                </Button>
              </Upload>
            </Space>
          </Can>
        </Space>
      ) : null}
    </Drawer>
  )
}
